<template>
  <nav class="lk-nav">
    <!-- Brand -->
    <div class="brand" @click="goto_home">
      <div class="brand-mark">
        <div class="brand-diamond"></div>
      </div>
      <span class="brand-name">linker<span class="brand-tld">.to</span></span>
    </div>

    <!-- Primary nav: Dashboard + manager pages + Tools menu (mock F1 nav). -->
    <div class="nav-items" :class="{ open: navOpen }">
      <router-link class="nav-item" to="/dashboard" @click="closeAll">Dashboard</router-link>
      <router-link class="nav-item" to="/share" @click="closeAll">Files</router-link>
      <router-link class="nav-item" to="/paste" @click="closeAll">Pastes</router-link>
      <div class="tools-menu">
        <button class="nav-item tools-trigger" :class="{ open: menuOpen }" @click.stop="toggleMenu">
          Tools <span class="caret">▾</span>
        </button>
        <div v-if="menuOpen" class="tools-dropdown" @click.stop>
          <component
            v-for="t in tools"
            :is="t.comingSoon ? 'div' : 'router-link'"
            :to="t.comingSoon ? undefined : t.createRoute"
            :key="t.id"
            class="tools-item"
            :class="{ soon: t.comingSoon }"
            @click="closeAll"
          >
            <span class="ti-icon">{{ t.icon }}</span>
            <span class="ti-body">
              <span class="ti-label">{{ t.label }}<span v-if="t.comingSoon" class="ti-soon">Soon</span></span>
              <span class="ti-desc">{{ t.desc }}</span>
            </span>
          </component>
        </div>
      </div>
    </div>

    <div class="spacer"></div>

    <!-- Mobile hamburger (three ink bars, mock F1m) -->
    <button
      class="hamburger"
      :class="{ on: navOpen }"
      aria-label="Menu"
      :aria-expanded="navOpen ? 'true' : 'false'"
      @click.stop="navOpen = !navOpen"
    >
      <span></span><span></span><span></span>
    </button>

    <!-- Auth -->
    <div v-if="loading" class="spinner-container">
      <div class="spinner"></div>
    </div>
    <template v-else>
      <router-link v-if="!isAuthenticated" class="lk-btn signin" :to="{ name: 'Login', query: { redirect: $route.fullPath } }">Sign in</router-link>
      <router-link v-else class="user-chip" to="/profile" title="Account" aria-label="Account">
        <span v-if="initials" class="avatar-initials">{{ initials }}</span>
        <svg v-else class="avatar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      </router-link>
    </template>
  </nav>
</template>

<script>
import {defineComponent} from 'vue';
import {mapGetters, mapState} from "vuex";
import {tools} from "@/tools";

export default defineComponent({
  name: 'NavBar',
  data() {
    return { tools, menuOpen: false, navOpen: false };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    ...mapState('auth', ['loading', 'user']),
    // Ink-circle avatar initials, derived from the email's local part
    // ("jane.doe@…" → "JD", "priya@…" → "P").
    initials() {
      const email = this.user && this.user.email;
      if (!email) return '';
      const segments = email.split('@')[0].split(/[._-]+/).filter(Boolean);
      return segments.slice(0, 2).map((s) => s[0].toUpperCase()).join('');
    },
  },
  methods: {
    goto_home() {
      this.$router.push('/');
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    closeAll() {
      this.menuOpen = false;
      this.navOpen = false;
    },
  },
  mounted() {
    this._onDocClick = () => this.closeAll();
    document.addEventListener('click', this._onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this._onDocClick);
  },
  watch: {
    $route() { this.closeAll(); },
  },
});
</script>

<style scoped>
.lk-nav {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 9px;
  height: 58px;
  padding: 0 clamp(16px, 4vw, 28px);
  border-bottom: 1px solid var(--border);
  background: color-mix(in srgb, var(--card) 92%, transparent);
  backdrop-filter: blur(12px);
}

.brand {
  display: flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
}
.brand-mark {
  width: 24px;
  height: 24px;
  border-radius: var(--radius);
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
}
.brand-diamond {
  width: 9px;
  height: 9px;
  border: 2px solid var(--paper);
  border-radius: 2.5px;
  transform: rotate(45deg);
}
.brand-name {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 21px;
  letter-spacing: -.01em;
  color: var(--text);
}
.brand-tld { color: var(--accent); }

.nav-items {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 26px;
}
.nav-item {
  border: none;
  background: transparent;
  color: var(--muted);
  font-size: 13.5px;
  font-weight: 500;
  padding: 7px 13px;
  border-radius: 5px;
  cursor: pointer;
  text-decoration: none;
  transition: background .15s, color .15s;
  white-space: nowrap;
  font-family: inherit;
}
.nav-item:hover { color: var(--text); background: var(--surface); }
.nav-item.router-link-active:not([href^="http"]) {
  color: var(--text);
  background: var(--accent-soft);
  font-weight: 600;
}

/* Tools dropdown */
.tools-menu { position: relative; }
.tools-trigger { display: inline-flex; align-items: center; gap: 5px; }
.tools-trigger.open { color: var(--text); background: var(--surface); font-weight: 600; }
.caret { font-size: 10px; color: var(--faint); }
.tools-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 260px;
  background: var(--card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  animation: lk-pop .15s ease both;
  z-index: 50;
}
.tools-item {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 10px;
  border-radius: var(--radius);
  text-decoration: none;
  color: var(--text);
  transition: background .12s;
}
a.tools-item:hover { background: var(--surface); }
.tools-item.soon { opacity: .65; cursor: default; }
.ti-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius);
  background: var(--accent-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}
.ti-body { display: flex; flex-direction: column; min-width: 0; }
.ti-label { font-size: 13.5px; font-weight: 600; display: flex; align-items: center; gap: 7px; }
.ti-desc { font-size: 12px; color: var(--muted); }
.ti-soon {
  font-size: 9.5px;
  font-weight: 600;
  color: var(--warn);
  background: var(--warn-soft);
  padding: 1px 5px;
  border-radius: var(--radius);
  text-transform: uppercase;
  letter-spacing: .03em;
}

.spacer { flex: 1; }

.signin { padding: 8px 15px; }

.user-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: var(--ink);
  text-decoration: none;
  color: var(--paper);
  transition: background .18s;
  flex-shrink: 0;
}
.user-chip:hover { background: var(--accent); color: var(--on-accent); }
.avatar-icon { width: 17px; height: 17px; }
.avatar-initials { font-size: 12.5px; font-weight: 600; letter-spacing: .02em; }

/* Hamburger — hidden on desktop */
.hamburger {
  display: none;
  flex-direction: column;
  gap: 3.5px;
  border: none;
  background: transparent;
  padding: 8px 6px;
  cursor: pointer;
}
.hamburger span {
  width: 18px;
  height: 2px;
  background: var(--muted);
  transition: transform .18s, opacity .18s;
}
.hamburger.on span:nth-child(1) { transform: translateY(5.5px) rotate(45deg); }
.hamburger.on span:nth-child(2) { opacity: 0; }
.hamburger.on span:nth-child(3) { transform: translateY(-5.5px) rotate(-45deg); }

.spinner-container {
  display: flex;
  align-items: center;
  height: 32px;
}
.spinner {
  border: 2px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: lk-spin .8s linear infinite;
}

/* Mobile (mock F1m): nav collapses behind the hamburger; brand left, burger +
   avatar right. The open menu drops below the bar as a card. */
@media (max-width: 680px) {
  .hamburger { display: flex; }
  .nav-items {
    display: none;
    position: absolute;
    top: calc(100% + 1px);
    left: 0;
    right: 0;
    margin-left: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    background: var(--card);
    border-bottom: 1px solid var(--border);
    padding: 10px clamp(16px, 4vw, 28px) 14px;
    box-shadow: var(--shadow-lg);
    animation: lk-up .15s ease both;
  }
  .nav-items.open { display: flex; }
  .nav-item { padding: 10px 13px; }
  .tools-dropdown {
    position: static;
    box-shadow: none;
    border: 1px solid var(--border);
    margin-top: 4px;
  }
}
</style>
